import React, { useEffect, useState, useRef } from "react";
import { Carousel, Button } from "antd";
import BannerSkeleton from "./skeletons/BannerSkeleton";
import { capitalizeFirst, renderSafeText } from '../util/unitFormatter';
import { useNavigate } from "react-router-dom";
import { IconChevronLeft, IconChevronRight } from "@tabler/icons-react";
import "../css/HomeSlider.css";
import PublicServ from "../service/PublicServ";
import SafeImage from "./SafeImage";
import { usePreference } from "../context/PreferenceContext";

const HomeSlider = ({ category, single = false, only = false }) => {
    const { t } = usePreference();
    const [banners, setBanners] = useState([]);
    const [loading, setLoading] = useState(false);
    const navigate = useNavigate();
    const carouselRef = useRef(null);

    useEffect(() => {
        const fetchBanners = async () => {
            setLoading(true);
            try {
                const res = await PublicServ.getBanners({ category });
                let data = res.data || [];
                if (single && data.length > 0) {
                    data = [data[0]];
                }
                setBanners(data);
            } catch (err) {
                console.error("Error fetching banners:", err);
            } finally {
                setLoading(false);
            }
        };
        fetchBanners();
    }, [category, single]);

    const handleBannerClick = (banner) => {
        if (banner.category) {
            navigate(`/category/${encodeURIComponent(banner.category.name)}`);
        } else if (banner.link) {
            if (banner.link.startsWith('http')) {
                window.open(banner.link, '_blank');
            } else {
                navigate(banner.link);
            }
        }
    };

    if (loading) return <div className="slider-loading"><BannerSkeleton /></div>;
    if (banners.length === 0) return null;

    const settings = {
        autoplay: !single,
        autoplaySpeed: 5000,
        dots: !single,
        fade: true,
        infinite: !single,
        speed: 800,
        slidesToShow: 1,
        slidesToScroll: 1,
    };

    return (
        <div className={`home-slider-container ${single ? 'single-image-mode' : ''} ${only ? 'only-banner-mode' : ''}`}>
            {/* Custom Navigation Buttons like CategorySwiper */}
            {!single && banners.length > 1 && (
                <>
                    <button className="slider-nav slider-prev" onClick={() => carouselRef.current.prev()}>
                        <IconChevronLeft size={24} />
                    </button>
                    <button className="slider-nav slider-next" onClick={() => carouselRef.current.next()}>
                        <IconChevronRight size={24} />
                    </button>
                </>
            )}

            <Carousel ref={carouselRef} {...settings}>
                {banners.map((banner, index) => (
                    <div className="slide-item" key={banner._id || index}>
                        <div className="slide-content" onClick={() => handleBannerClick(banner)}>
                            <div className="slide-image-wrapper">
                                <SafeImage src={banner.imageUrl} variant="banner" className="slide-image" alt={banner.title} placeholderText="Banner" />
                            </div>

                            {!only && (
                                <div className="slide-overlay">
                                    {banner.category && (
                                        <div className="slide-category-badge">
                                            {banner.category.imageUrl && (
                                                <SafeImage
                                                    src={banner.category.imageUrl}
                                                    alt={banner.category.name}
                                                    variant="square"
                                                    className="slide-category-icon"
                                                    placeholderText=""
                                                />
                                            )}
                                            <span className="slide-category-name">
                                                {renderSafeText(banner.category.name)}
                                            </span>
                                        </div>
                                    )}

                                    <h2 className="slide-title">{banner.title}</h2>

                                    <div className="slide-actions">
                                        <Button
                                            type="primary"
                                            size="large"
                                            className="slide-button"
                                            onClick={(e) => {
                                                e.stopPropagation();
                                                handleBannerClick(banner);
                                            }}
                                        >
                                            {t('view_products')}
                                        </Button>
                                    </div>
                                </div>
                            )}
                        </div>
                    </div>
                ))}
            </Carousel>
        </div>
    );
};

export default HomeSlider;
